import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { CalendarToday, CircleNotifications, LocationOn } from '@mui/icons-material';

import { useAuth } from '../../context/AuthContext';
import CommandPalette from '../../command/CommandPalette';
// Imported file-by-file, and `components/mission` deliberately has no barrel.
//
// A directory import carries an extra resolution step (mission ->
// mission/index.ts) which the dev server caches as a *failure* if the folder
// was ever compiled before the index existed. That happened here — the
// components were written a few minutes before the barrel — and it survived
// clearing `node_modules/.cache`, because that is only webpack's cache while
// ForkTsChecker holds its own in-memory TypeScript program.
//
// The barrel was deleted rather than left unused, so nobody can "tidy" these
// back into a directory import and reintroduce the same failure.
import BusinessPulse from '../../components/mission/BusinessPulse';
import BusinessFlowStrip from '../../components/mission/BusinessFlowStrip';
import AttentionPanel from '../../components/mission/AttentionPanel';
import StratifyAiPanel from '../../components/mission/StratifyAiPanel';
import BusinessStoryCard from '../../components/mission/BusinessStoryCard';
import QuickActionDock from '../../components/mission/QuickActionDock';
import {
  PERSPECTIVES, Perspective, pulseFor, SCHEDULE, ACTIVITY,
} from '../../components/mission/missionData';
import { s, cardSx, focusRing, reduceMotion } from '../../components/mission/missionTokens';

const greeting = (d: Date) => {
  const h = d.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const TONE = {
  primary: s.primary,
  violet: s.violet,
  success: s.success,
  warning: s.warning,
} as const;

/**
 * Opening perspective per role.
 *
 * This preserves the previous `RoleHome` behaviour, which redirected each role
 * into the area it runs. Mission Control is now everyone's home, so the role
 * no longer picks a *destination* — it picks the lens the home screen opens
 * on. The user can switch freely from there, which the redirect never allowed.
 */
const ROLE_PERSPECTIVE: Record<string, Perspective> = {
  FINANCE: 'Financial',
  MANAGEMENT: 'Executive',
  SALES: 'Sales',
  PROCUREMENT: 'Supply Chain',
  STORE: 'Operations',
  PRODUCTION: 'Operations',
  QUALITY: 'Operations',
  MAINTENANCE: 'Operations',
  HR: 'People',
};

/**
 * Mission Control — the authenticated home screen.
 *
 * Replaces `RoleHome`, which was only a redirect: there was no home, so every
 * user landed inside whichever module their role implied and never saw the
 * business as one thing. This screen is organised Context → Insight →
 * Decision → Action: who/where/when, then the pulse, then what changed and
 * what needs attention, then the actions that answer it.
 *
 * All figures come from `components/mission/missionData.ts` behind typed
 * interfaces, so swapping in live endpoints does not touch this layout.
 */
const MissionControlPage: React.FC = () => {
  const { user } = useAuth();
  const [perspective, setPerspective] = useState<Perspective>(
    () => ROLE_PERSPECTIVE[(user?.role || '').toUpperCase()] ?? 'Executive'
  );
  const [askOpen, setAskOpen] = useState(false);
  const [askQuery, setAskQuery] = useState('');

  const now = useMemo(() => new Date(), []);
  const firstName = (user?.name || 'there').split(' ')[0];
  const metrics = useMemo(() => pulseFor(perspective), [perspective]);

  /**
   * The AI panel deliberately does not answer inline — it hands the question to
   * the existing command palette, so the product has exactly one place where a
   * natural-language question is resolved.
   *
   * Local `useState` rather than `useCommandPalette()` on purpose: that hook
   * installs its own Ctrl/Cmd+K listener, and the header already mounts one.
   * Using it here would bind the shortcut twice and open two dialogs.
   */
  const handleAsk = (question: string) => {
    setAskQuery(question);
    setAskOpen(true);
  };

  return (
    <Box sx={{ backgroundColor: s.canvas, minHeight: '100%', p: { xs: 2, md: 3 } }}>
      {/* ---- Context: who, where, when ---- */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'flex-end' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography
            sx={{ fontSize: 'clamp(1.5rem, 2.6vw, 2rem)', fontWeight: 800, color: s.heading, letterSpacing: '-0.025em' }}
          >
            {greeting(now)}, {firstName}. 👋
          </Typography>
          <Typography sx={{ fontSize: '0.92rem', color: s.body, mt: 0.5 }}>
            Here&apos;s the complete picture of your business today.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CalendarToday sx={{ fontSize: 15, color: s.muted }} />
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: s.heading, lineHeight: 1.3 }}>
                {now.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: s.muted, lineHeight: 1.3 }}>
                {now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.9,
              borderRadius: 2, backgroundColor: s.paper, border: `1px solid ${s.border}`,
              boxShadow: s.shadowSm,
            }}
          >
            <LocationOn sx={{ fontSize: 15, color: s.success }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: s.heading }}>Main Warehouse</Typography>
          </Box>
        </Box>
      </Box>

      {/* ---- Perspective: changes what the workspace is about ---- */}
      <Box
        role="tablist"
        aria-label="Workspace perspective"
        sx={{
          display: 'flex',
          gap: 0.75,
          mb: 2.5,
          overflowX: 'auto',
          pb: 0.5,
          // A perspective bar is a single decision; it must not wrap into a
          // two-line block that pushes the pulse below the fold.
          '&::-webkit-scrollbar': { height: 0 },
          scrollbarWidth: 'none',
        }}
      >
        {PERSPECTIVES.map((p) => {
          const active = p === perspective;
          return (
            <Box
              key={p}
              component="button"
              role="tab"
              aria-selected={active}
              onClick={() => setPerspective(p)}
              sx={{
                flexShrink: 0,
                px: 2,
                py: 0.9,
                cursor: 'pointer',
                borderRadius: 5,
                border: `1px solid ${active ? 'transparent' : s.border}`,
                backgroundColor: active ? s.primary : s.paper,
                color: active ? '#fff' : s.body,
                fontSize: '0.8rem',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                transition: 'background-color .2s ease, color .2s ease, transform .2s ease',
                '&:hover': { transform: active ? 'none' : 'translateY(-1px)', borderColor: s.borderStrong },
                ...focusRing,
                ...reduceMotion,
              }}
            >
              {p}
            </Box>
          );
        })}
      </Box>

      {/* ---- Insight: the pulse ---- */}
      <Box sx={{ mb: 2 }}>
        <BusinessPulse key={perspective} metrics={metrics} />
      </Box>

      {/* ---- Action dock ---- */}
      <Box sx={{ mb: 2 }}>
        <QuickActionDock />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 340px' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        {/* ---- Main column ---- */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <BusinessFlowStrip />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' },
              gap: 2,
              alignItems: 'stretch',
            }}
          >
            <AttentionPanel />
            <BusinessStoryCard />
          </Box>
        </Box>

        {/* ---- Intelligence rail ---- */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <StratifyAiPanel onAsk={handleAsk} />

          <Box sx={{ ...cardSx, p: 2 }}>
            <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: s.heading, mb: 1.5 }}>
              Today&apos;s Schedule
            </Typography>
            {SCHEDULE.map((item) => (
              <Box key={item.time} sx={{ display: 'flex', gap: 1.25, mb: 1.5, '&:last-of-type': { mb: 0 } }}>
                <Typography sx={{ fontSize: '0.7rem', color: s.muted, fontWeight: 600, width: 62, flexShrink: 0, pt: 0.2 }}>
                  {item.time}
                </Typography>
                <Box sx={{ width: 3, borderRadius: 2, backgroundColor: TONE[item.tone], flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: s.heading, lineHeight: 1.35 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: s.muted, lineHeight: 1.35 }}>{item.detail}</Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ ...cardSx, p: 2 }}>
            <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: s.heading, mb: 1.5 }}>
              Recent Activity
            </Typography>
            {ACTIVITY.map((a) => (
              <Box key={a.title} sx={{ display: 'flex', gap: 1.25, mb: 1.35, '&:last-of-type': { mb: 0 } }}>
                <CircleNotifications sx={{ fontSize: 16, color: s.muted, flexShrink: 0, mt: 0.2 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: s.heading, lineHeight: 1.35 }}>
                    {a.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: s.muted, lineHeight: 1.35 }}>{a.detail}</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.66rem', color: s.muted, flexShrink: 0 }}>{a.when}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <CommandPalette open={askOpen} initialQuery={askQuery} onClose={() => setAskOpen(false)} />
    </Box>
  );
};

export default MissionControlPage;
